import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { GridService } from '@core/services';
import { EnvironmentService, EventService, ListService, fromList } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { ListOfValuesResponse, MultipleListOfValuesResponse } from '#types/list';

import { CONSTANTS } from '@item/constants';
import { ItemService } from '@item/services';

import * as ItemActions from './item.actions';

@Injectable()
export class ItemEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private itemService: ItemService,
    private gridService: GridService,
    private environmentService: EnvironmentService,
    private listService: ListService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ItemActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectItemLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(ItemActions.RequestItemLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  RequestItemCustomerProductGroupList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemActions.RequestItemCustomerProductGroupList),
      exhaustMap((parameters) => {
        return this.listService
          .getListOfValuesWithParameters<ListOfValuesResponse>('item', 'customerProductGroup', { value01: parameters.custid })
          .pipe(
            this.eventService.indicate(CONSTANTS.LIST_ITEM_CUSTOMER_PRODUCT_GROUP),
            map((list) => ItemActions.RequestItemCustomerProductGroupListSuccess({ list, custid: parameters.custid })),
            catchError((error) => of(ItemActions.RequestItemCustomerProductGroupListFailure(error)))
          );
      })
    );
  });

  RequestItemCustomerRateGroupList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemActions.RequestItemCustomerRateGroupList),
      exhaustMap((parameters) => {
        return this.listService
          .getListOfValuesWithParameters<ListOfValuesResponse>('item', 'customerRateGroup', { value01: parameters.custid })
          .pipe(
            this.eventService.indicate(CONSTANTS.LIST_ITEM_CUSTOMER_RATE_GROUP),
            map((list) => ItemActions.RequestItemCustomerRateGroupListSuccess({ list, custid: parameters.custid })),
            catchError((error) => of(ItemActions.RequestItemCustomerRateGroupListFailure(error)))
          );
      })
    );
  });

  RequestlotRequiredCheckOnItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemActions.RequestlotRequiredCheckOnItem),
      exhaustMap(({ custid, item }) => {
        return this.itemService.getItemLotRequiredCheck({ custid, item }).pipe(
          this.eventService.indicate(CONSTANTS.ITEM_LOT_REQUIRED_CHECK),
          map((data) => ItemActions.RequestlotRequiredCheckOnItemSuccess({ data })),
          catchError((error) => of(ItemActions.RequestlotRequiredCheckOnItemFailure(error)))
        );
      })
    );
  });

  RequestItemLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ItemActions.RequestItemLists),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('item').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => ItemActions.RequestItemListsSuccess({ lists })),
          catchError((error) => of(ItemActions.RequestItemListsFailure(error)))
        );
      })
    );
  });

  LookupRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ItemActions.ItemLookupDialogRequestData,
        ItemActions.ItemLookupDialogRequestDataWithFilters,
        ItemActions.ItemLookupDialogRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.itemService.getItemLookup({ filters, page, rows, sorts }).pipe(
          map((data) => ItemActions.ItemLookupDialogRequestDataSuccess({ id, data })),
          catchError((_error) => of(ItemActions.ItemLookupDialogRequestDataFailure({ id })))
        )
      )
    );
  });
}
